import { useEffect, useState } from 'react'
import axios from 'lib/axios'
import Input from 'views/components/UI/TextInput'
import AddIcon from '@mui/icons-material/Add'
import styled from 'styled-components'
import { FormControlLabel, Switch } from '@mui/material'
import { Button } from 'views/components/UI/Buttons'
import {
  SettingsActions,
  SettingsCard,
  SettingsFeedback,
  SettingsFields,
  SettingsHeader,
  SettingsStatus,
  SettingsTitle,
} from './SettingsSection.styled'

const CredentialForm = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 18px;
  margin: 0;

  ${SettingsFields} {
    grid-template-columns: minmax(0, 1fr);
  }

  ${SettingsActions} {
    justify-content: flex-start;
    margin-top: 0;
  }

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const IntegrationAccessControl = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin: 0 0 22px;

  p {
    margin: 0;
    color: #718096;
    font-size: 13px;
  }

  .MuiFormControlLabel-label {
    color: #1f344d;
    font-size: 14px;
    font-weight: 600;
  }
`

const RotationGuide = styled.div`
  margin-bottom: 22px;
  padding: 16px 18px;
  border: 1px solid #d8e6f1;
  border-radius: 12px;
  background: #f5f9fd;
  color: #536b84;
  font-size: 14px;
  line-height: 1.5;

  strong {
    color: #1f344d;
  }

  ol {
    display: grid;
    gap: 10px;
    margin: 12px 0 0;
    padding: 0;
    list-style: none;
    counter-reset: rotation-step;
  }

  li {
    position: relative;
    min-height: 28px;
    padding-left: 38px;
    counter-increment: rotation-step;
  }

  li::before {
    position: absolute;
    top: 0;
    left: 0;
    display: grid;
    width: 26px;
    height: 26px;
    place-items: center;
    border: 1px solid #c9dced;
    border-radius: 50%;
    background: #fff;
    color: #28799d;
    content: counter(rotation-step);
    font-size: 12px;
    font-weight: 700;
  }
`

const ModeTabs = styled.div`
  display: inline-flex;
  gap: 4px;
  margin: 0 0 22px;
  padding: 4px;
  border-radius: 12px;
  background: #eef4fa;

  button {
    border: 0;
    border-radius: 9px;
    padding: 10px 18px;
    background: transparent;
    color: #536b84;
    cursor: pointer;
    font: inherit;
    font-size: 14px;
    font-weight: 700;
  }

  button[aria-selected='true'] {
    background: #fff;
    color: #1f344d;
    box-shadow: 0 2px 8px rgba(23, 55, 77, 0.12);
  }

  button:focus-visible {
    outline: 2px solid #28799d;
    outline-offset: 2px;
  }
`

const CredentialsList = styled.ul`
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid #e4ebf3;
`

const CredentialItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid #e4ebf3;
  color: #536b84;
  font-size: 14px;

  strong {
    display: block;
    margin-bottom: 4px;
    color: #172b4d;
  }

  > div:last-child {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }
`

const KeyPanel = styled.div`
  margin-top: 18px;
  padding: 16px 18px;
  border: 1px solid #d8e6f1;
  border-radius: 12px;
  background: #f5f9fd;
  color: #536b84;

  p {
    margin: 0 0 10px;
  }

  code {
    display: block;
    overflow-wrap: anywhere;
    color: #172b4d;
  }

  > div {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }
`

interface Credential {
  id: number
  name: string
  key_prefix: string
  scope: string
  created_at: string
  last_used_at: string | null
  revoked_at: string | null
  expires_at: string | null
}

const isActive = (credential: Credential) =>
  !credential.revoked_at && (!credential.expires_at || new Date(credential.expires_at).getTime() > Date.now())

interface Props {
  companyId: number
  platformCanManageAccess?: boolean
}

interface PanelProps extends Props {
  mode: 'live' | 'sandbox'
  onModeChange: (mode: 'live' | 'sandbox') => void
}

const integrationRequestConfig = {
  preserveSessionOnForbidden: true,
}

const IntegrationCredentialsPanel = ({ companyId, platformCanManageAccess = false, mode, onModeChange }: PanelProps) => {
  const sandbox = mode === 'sandbox'
  const [credentials, setCredentials] = useState<Credential[]>([])
  const [apiEnabled, setApiEnabled] = useState<boolean | null>(null)
  const [name, setName] = useState('')
  const [newKey, setNewKey] = useState('')
  const [newKeyExpiresAt, setNewKeyExpiresAt] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const load = async () => {
    setLoaded(false)
    try {
      const [credentialsResponse, statusResponse] = await Promise.all([
        axios.get(
          `/company/${companyId}/integration-credentials${sandbox ? '?mode=sandbox' : ''}`,
          integrationRequestConfig
        ),
        axios.get(
          `/company/${companyId}/${sandbox ? 'user-import-sandbox' : 'user-import-api'}`,
          integrationRequestConfig
        ),
      ])
      setCredentials(credentialsResponse.data.data)
      setApiEnabled(statusResponse.data.data.enabled)
      setError('')
      setLoaded(true)
    } catch {
      setCredentials([])
      setApiEnabled(null)
      setError('No fue posible consultar las credenciales.')
    }
  }

  useEffect(() => {
    setNewKey('')
    setNewKeyExpiresAt(null)
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, mode])

  const create = async () => {
    if (name.trim().length < 3 || busy || !apiEnabled) return
    setBusy(true)
    setNewKey('')
    setNewKeyExpiresAt(null)
    try {
      const response = await axios.post(
        `/company/${companyId}/integration-credentials`,
        { name: name.trim(), mode },
        integrationRequestConfig
      )
      setNewKey(response.data.data.key)
      setNewKeyExpiresAt(response.data.data.expires_at)
      setName('')
      await load()
    } catch (requestError) {
      const status = (requestError as { response?: { status?: number } }).response?.status
      setError(status === 409 ? (sandbox ? 'Revoca la clave de prueba antes de crear otra.' : 'Termina la rotación actual antes de crear otra credencial.') : 'No fue posible crear la credencial.')
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (credentialId: number) => {
    if (busy || !window.confirm('¿Revocar esta credencial? El cliente perderá acceso de inmediato.')) return
    setBusy(true)
    setNewKey('')
    setNewKeyExpiresAt(null)
    try {
      await axios.delete(`/company/${companyId}/integration-credentials/${credentialId}`, integrationRequestConfig)
      await load()
    } catch {
      setError('No fue posible revocar la credencial.')
    } finally {
      setBusy(false)
    }
  }

  const updateApiAccess = async (enabled: boolean) => {
    if (busy || (!enabled && !window.confirm(sandbox ? 'Al desactivar sandbox se revocará su clave de prueba.' : 'Al desactivar la API se revocarán todas las credenciales y las llamadas dejarán de autorizarse. Para volver a usarla, habrá que crear claves nuevas.'))) return
    setBusy(true)
    setError('')
    try {
      await axios.put(
        `/company/${companyId}/${sandbox ? 'user-import-sandbox' : 'user-import-api'}`,
        { enabled },
        integrationRequestConfig
      )
      setNewKey('')
      setNewKeyExpiresAt(null)
      await load()
    } catch {
      setError('No fue posible actualizar el acceso a la API.')
    } finally {
      setBusy(false)
    }
  }

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(newKey)
    } catch {
      setError('No fue posible copiar la clave. Selecciónala y cópiala manualmente.')
    }
  }

  const activeCredentials = credentials.filter(isActive)
  const rotating = activeCredentials.some((credential) => Boolean(credential.expires_at))
  const canCreate = loaded && apiEnabled === true && (sandbox ? activeCredentials.length === 0 : activeCredentials.length < 2 && !rotating)

  return (
    <SettingsCard>
      <SettingsHeader>
        <SettingsTitle>
          <h3>Integración API de usuarios</h3>
          <p>Gestiona la importación real y sus pruebas para esta empresa.</p>
        </SettingsTitle>
        <SettingsStatus tone={loaded && apiEnabled ? 'success' : 'neutral'}>
          {loaded ? (apiEnabled ? (sandbox ? 'Sandbox habilitado' : 'API habilitada') : (sandbox ? 'Sandbox deshabilitado' : 'API deshabilitada')) : 'Estado no disponible'}
        </SettingsStatus>
      </SettingsHeader>
      <ModeTabs role="tablist" aria-label="Modo de integración API">
        {(['live', 'sandbox'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={mode === option}
            disabled={busy}
            onClick={() => {
              if (option === mode) return
              if (newKey && !window.confirm('La clave completa dejará de mostrarse al cambiar de pestaña. Guárdala antes de continuar.')) return
              onModeChange(option)
            }}
          >
            {option === 'live' ? 'Importación real' : 'Sandbox'}
          </button>
        ))}
      </ModeTabs>
      {!sandbox && <RotationGuide>
        <strong>Cómo funciona la rotación</strong>
        <ol>
          <li>Hay una credencial principal. Puedes crear una segunda durante 7 días para cambiarla sin interrumpir la integración.</li>
          <li>Configura y prueba la nueva clave; después revoca la anterior. La nueva pasa a ser la principal.</li>
          <li>Si no completas el cambio, la temporal caduca y la principal sigue funcionando. La clave completa solo se muestra al crearla.</li>
        </ol>
      </RotationGuide>}
      {sandbox && <RotationGuide>
        <strong>Cómo funciona la prueba</strong>
        <ol>
          <li>Activa sandbox y crea una clave de prueba que caduca en 7 días. Solo puede haber una activa.</li>
          <li>Envía hasta 50 usuarios a <code>/integrations/v1/people/sandbox-batch</code> con esa clave.</li>
          <li>Se aplican las mismas reglas que en producción y se devuelve el resultado del lote. Al terminar se revierten las altas y cambios de usuarios; el uso de la clave y los identificadores de secuencia sí pueden avanzar.</li>
        </ol>
      </RotationGuide>}
      {(sandbox || platformCanManageAccess) && (
        <IntegrationAccessControl>
          <FormControlLabel
            control={(
              <Switch
                checked={apiEnabled === true}
                disabled={!loaded || busy}
                onChange={(event) => void updateApiAccess(event.target.checked)}
              />
            )}
            label={sandbox ? 'Activar sandbox' : 'Permitir el acceso a la API'}
          />
          <p>{sandbox ? 'Al desactivarlo se revoca la clave de prueba. La API principal no cambia.' : 'Al desactivarla se revocan todas las claves. Para reactivarla, habrá que crear credenciales nuevas.'}</p>
        </IntegrationAccessControl>
      )}
      {apiEnabled && <CredentialForm
        onSubmit={(event) => {
          event.preventDefault()
          void create()
        }}
      >
        <SettingsFields>
          <Input
            name="integrationName"
            type="text"
            label="Nombre de la credencial"
            value={name}
            inputProps={{ maxLength: 100 }}
            onChange={(event) => setName(event.target.value)}
          />
        </SettingsFields>
        <SettingsActions>
          <Button
            type="submit"
            disabled={busy || !canCreate || name.trim().length < 3}
            leadingIcon={<AddIcon fontSize="small" />}
          >
            {busy ? 'Procesando...' : sandbox ? 'Crear clave de prueba' : activeCredentials.length ? 'Rotar credencial' : 'Crear credencial'}
          </Button>
        </SettingsActions>
      </CredentialForm>}
      {loaded && !apiEnabled && (
        <SettingsFeedback>
          {sandbox ? 'Activa el sandbox para poder crear una clave de prueba. La API principal debe estar habilitada.' : platformCanManageAccess
            ? 'Activa el acceso a la API para poder crear credenciales.'
            : 'El administrador de plataforma aún no ha habilitado el acceso a esta API.'}
        </SettingsFeedback>
      )}
      {!sandbox && rotating && <SettingsFeedback>Hay una rotación en curso. Revoca la credencial anterior cuando el cliente use la nueva.</SettingsFeedback>}
      {!sandbox && loaded && activeCredentials.length > 1 && !rotating && (
        <SettingsFeedback error>Hay varias credenciales anteriores activas. Revoca las sobrantes antes de crear otra.</SettingsFeedback>
      )}
      {newKey && (
        <KeyPanel role="status">
          <p>
            Guarda esta clave ahora. No volverá a mostrarse.
            {newKeyExpiresAt && ` La clave ${sandbox ? 'de prueba' : 'de rotación'} caduca el ${new Date(newKeyExpiresAt).toLocaleString()}.`}
          </p>
          <code>{newKey}</code>
          <div>
            <Button variant="secondary" size="sm" margin="0" onClick={copyKey}>Copiar clave</Button>
            <Button variant="ghost" size="sm" margin="0" onClick={() => setNewKey('')}>Ocultar clave</Button>
          </div>
        </KeyPanel>
      )}
      {error && <SettingsFeedback error role="alert">{error}</SettingsFeedback>}
      <CredentialsList>
        {loaded && credentials.length === 0 && <CredentialItem>No hay credenciales creadas.</CredentialItem>}
        {credentials.map((credential) => (
          <CredentialItem key={credential.id}>
            <div>
              <strong>{credential.name}</strong>
              <span>{credential.key_prefix} · Último uso: {credential.last_used_at ? new Date(credential.last_used_at).toLocaleString() : 'sin uso'}</span>
            </div>
            <div>
              <SettingsStatus tone={credential.revoked_at || !isActive(credential) ? 'neutral' : credential.expires_at ? 'warning' : 'success'}>
                {credential.revoked_at ? 'Revocada' : !isActive(credential) ? 'Caducada' : credential.expires_at ? `${sandbox ? 'Prueba' : 'Rotación'} hasta ${new Date(credential.expires_at).toLocaleString()}` : 'Principal'}
              </SettingsStatus>
              {isActive(credential) && (
                <Button variant="secondary" size="sm" margin="0" disabled={busy} onClick={() => revoke(credential.id)}>
                  Revocar
                </Button>
              )}
            </div>
          </CredentialItem>
        ))}
      </CredentialsList>
    </SettingsCard>
  )
}

const IntegrationCredentialsSection = ({ companyId, platformCanManageAccess = false }: Props) => {
  const [mode, setMode] = useState<'live' | 'sandbox'>('live')
  return (
    <IntegrationCredentialsPanel
      key={mode}
      companyId={companyId}
      platformCanManageAccess={platformCanManageAccess}
      mode={mode}
      onModeChange={setMode}
    />
  )
}

export default IntegrationCredentialsSection
